<template>
	<div class="d3-history-chart">
		<div class="chart-container" ref="container">
			<svg class="svg" :width="container.width" :height="container.height">
				<defs>
					<mask id="mask">
						<rect :x="margin.left - 2" :y="margin.top" :width="padded.width + 5" :height="padded.height" fill="#fff"/>
					</mask>
				</defs>
				<g class="data" mask="url(#mask)">
					<g class="bars-default" :style="{ transform: `translate(${margin.left}px, ${margin.top}px)` }" v-if="!isBloodPressure"/>
					<g class="bars-min" :style="{ transform: `translate(${margin.left}px, ${margin.top}px)` }" v-if="isBloodPressure"/>
					<g class="bars-max" :style="{ transform: `translate(${margin.left}px, ${margin.top}px)` }" v-if="isBloodPressure"/>
				</g>
				<rect class="rect"></rect>
			</svg>
		</div>
		<div v-if="!readings.length" class="empty">{{ $t('messages.general.no_values_for_this_date') }}</div>
	</div>
</template>

<script>
import * as d3 from '@/utils/d3'
import Readings from '@/utils/readings'
export default {
	props: {
		type: {
			type: Number,
			default: 0
		},
		readings: {
			type: Array,
			default: () => []
		},
		intervalStart: String,
		intervalEnd: String,
		onClickPrev: Function,
		onClickNext: Function,
		highlight: Object
	},
	data () {
		return {
			container: {
				width: 0,
				height: 0
			},
			margin: {
				left: 20,
				right: 20,
				top: 0,
				bottom: 20
			},
			scaled: {
				x: null,
				y: null
			},
			axis: {
				x: null,
				y: null
			},
			gAxisX: null
		}
	},
	computed: {
		padded () {
			const width = Math.max(this.container.width - this.margin.left - this.margin.right, 0)
			const height = Math.max(this.container.height - this.margin.top - this.margin.bottom, 0)
			return { width, height }
		},
		data () {
			return this.readings.length ? this.readings.slice(0) : []
		},
		minDate () {
			return new Date(this.intervalStart)
		},
		maxDate () {
			return new Date(this.intervalEnd)
		},
		minValue () {
			return d3.min(this.data, d => d.values.last || d.values.value || d.values.diastolic_last || (d.values.fall ? 1 : 0)) || 0
		},
		maxValue () {
			return d3.max(this.data, d => d.values.last || d.values.value || d.values.systolic_last || (d.values.fall ? 1 : 0)) || 100
		},
		isBloodPressure () {
			return this.type === Readings.TYPE_BLOOD_PRESSURE
		},
		valueMarginMinMax () {
			switch (parseInt(this.type)) {
				case Readings.TYPE_TEMPERATURE:
					return { min: 1, max: 1 }
				case Readings.TYPE_BLOOD_SATURATION:
					return { min: 5, max: 0 }
				case Readings.TYPE_FALLS:
					return { min: 1, max: 0.5 }
				default:
					return { min: 5, max: 5 }
			}
		}
	},
	watch: {
		highlight (val, oldVal) {
			if (val.id != oldVal.id) {
				this.update()
			}
		}
	},
	methods: {
		onResize () {
			this.container.width = Math.max(this.$refs.container.offsetWidth, 0)
			this.container.height = Math.max(this.$refs.container.offsetHeight, 0)
			this.update()
		},
		update () {
			this.scaled.x = d3.scaleTime()
				.domain([this.minDate, this.maxDate])
				.range([10, this.padded.width - 20])

			this.scaled.y = d3.scaleLinear()
				.domain([this.minValue - this.valueMarginMinMax.min, this.maxValue + this.valueMarginMinMax.max]).nice(3)
				.range([this.padded.height, 0])

			this.scaled.y.ticks(3)

			const svg = d3.select('.d3-history-chart .svg')

			this.axis.x = d3.axisBottom()
				.scale(this.scaled.x)
				.tickSize(0)
				.tickPadding(6)
				.tickFormat(d3.timeFormat('%H'))

			this.axis.y = d3.axisLeft()
				.scale(this.scaled.y)
				.tickSize(-this.padded.width)
				.ticks(3)
			
			d3.selectAll('.d3-history-chart .axis').remove()

			this.gAxisX = svg.append('g')
				.attr('class', 'x axis')
				.attr('transform', `translate(${this.margin.left}, ${this.margin.top + this.padded.height})`)
				.call(this.axis.x)

			svg.append('g')
				.attr('class', 'y axis')
				.attr('transform', `translate(${this.margin.left - 5}, ${this.margin.top})`)
				.call(this.axis.y)

			svg.selectAll('.y.axis .tick text')
				.style('text-anchor', 'end')
				.attr('x', this.padded.width + 10)
				.attr('y', 9)

			svg.selectAll('.y.axis .tick line')
				.attr('x2', this.padded.width + 10)

			this.updateCharts(this.scaled.x, this.scaled.y)
		},
		updateBloodPressure (xScale, yScale) {
			const highlightId = this.highlight ? this.highlight.id : -1
			// Min (Diastolic)
			const barsMin = d3.select('.d3-history-chart .bars-min')
				.selectAll('.bar')
				.data(this.data)
			barsMin.enter().append('rect').merge(barsMin)
				//.attr('class', 'bar')
				.attr('class', d => { return d.id === highlightId ? 'bar highlight' : 'bar' })
				.attr('x', d => { return xScale(new Date(d.datetime)) - 1 })
				.attr('y', d => { return yScale(d.values.diastolic_last) })
				.attr('width', 1)
				.attr('height', d => { return this.padded.height - yScale(d.values.diastolic_last) })
			barsMin.exit().remove()
			// Max (Systolic)
			const barsMax = d3.select('.d3-history-chart .bars-max')
				.selectAll('.bar')
				.data(this.data)
			barsMax.enter().append('rect').merge(barsMax)
				//.attr('class', 'bar')
				.attr('class', d => { return d.id === highlightId ? 'bar highlight' : 'bar' })
				.attr('x', d => { return xScale(new Date(d.datetime)) + 1 })
				.attr('y', d => { return yScale(d.values.systolic_last) })
				.attr('width', 1)
				.attr('height', d => { return this.padded.height - yScale(d.values.systolic_last) })
			barsMax.exit().remove()
		},
		updateCaloriesPedometer (xScale, yScale) {
			const highlightId = this.highlight ? this.highlight.id : -1
			const barsDefault = d3.select('.d3-history-chart .bars-default')
				.selectAll('.bar')
				.data(this.data)
			barsDefault.enter().append('rect').merge(barsDefault)
				//.attr('class', 'bar')
				.attr('class', d => { return d.id === highlightId ? 'bar highlight' : 'bar' })
				.attr('x', d => { return xScale(new Date(d.datetime)) })
				.attr('y', d => { return yScale(d.values.value) })
				.attr('width', 1)
				.attr('height', d => { return this.padded.height - yScale(d.values.value) })
			barsDefault.exit().remove()
		},
		updateFalls (xScale, yScale) {
			const highlightId = this.highlight ? this.highlight.id : -1
			const barsDefault = d3.select('.d3-history-chart .bars-default')
				.selectAll('.bar')
				.data(this.data)
			barsDefault.enter().append('rect').merge(barsDefault)
				//.attr('class', 'bar')
				.attr('class', d => { return d.id === highlightId ? 'bar highlight' : 'bar' })
				.attr('x', d => { return xScale(new Date(d.datetime)) })
				.attr('y', d => { return yScale(1) })
				.attr('width', 1)
				.attr('height', d => { return this.padded.height - yScale(1) })
			barsDefault.exit().remove()
		},
		updateDefault (xScale, yScale) {
			const highlightId = this.highlight ? this.highlight.id : -1
			const barsDefault = d3.select('.d3-history-chart .bars-default')
				.selectAll('.bar')
				.data(this.data)
			barsDefault.enter().append('rect').merge(barsDefault)
				//.attr('class', 'bar')
				.attr('class', d => { return d.id === highlightId ? 'bar highlight' : 'bar' })
				.attr('x', d => { return xScale(new Date(d.datetime)) })
				.attr('y', d => { return yScale(d.values.last) })
				.attr('width', 1)
				.attr('height', d => { return this.padded.height - yScale(d.values.last) })
			barsDefault.exit().remove()
		},
		updateCharts (xScale, yScale) {
			switch (this.type) {
				case Readings.TYPE_BLOOD_PRESSURE:
					this.updateBloodPressure(xScale, yScale)
					break
				case Readings.TYPE_CALORIES:
				case Readings.TYPE_PEDOMETER:
					this.updateCaloriesPedometer(xScale, yScale)
					break
				case Readings.TYPE_FALLS:
					this.updateFalls(xScale, yScale)
					break
				default:
					this.updateDefault(xScale, yScale)
			}
			// Bring charts to top layer (z index)
			d3.select('.d3-history-chart .data').raise()
			// Update x axis
			this.gAxisX.call(this.axis.x.scale(xScale));
		}
	},
	mounted () {
		window.addEventListener('resize', this.onResize)
		this.$root.$on('force-render-biometric-chart', this.onResize)
		this.onResize()
	},
	beforeDestroy () {
		window.removeEventListener('resize', this.onResize)
		this.$root.$off('force-render-biometric-chart', this.onResize)
	}
}
</script>



// WEBPACK FOOTER //
// src/components/watch/readings/biometric/widgets/history/BiometricWidgetHistoryChart.vue